import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../utils/supabase';
import { toast } from 'react-toastify';
import { getDateTags } from '../utils/dateTags';
import { awardXP } from '../utils/xpAwards';
import { useCachedData } from './useCachedData';
import { useToast } from '../context/ToastContext';
import type { FilterItem } from '../types/filterItem';
import { CACHE_KEYS } from '../constants/cacheKeys';
import { CareerCard } from '../types/careerCard';

const PAGE_SIZE = 20;

const transformToFilterItem = (card: CareerCard): FilterItem & { card: CareerCard } => {
  // Skip cards without cardid
  if (!card.cardid) {
    console.warn('Card missing cardid:', card);
    return null as any;
  }
  
  // Ensure card_data has all required fields and preserve the original structure
  const cardData = {
    ...card.card_data,
    title: card.card_data.title || 'Untitled Card',
    custom_tags: card.card_data.custom_tags || [],
    date_tags: card.card_data.date_tags || []
  };

  // Create a properly structured CareerCard object
  const fullCard: CareerCard = {
    ...card,
    cardid: card.cardid,
    card_data: cardData,
    card_version: card.card_version,
    created_at: card.created_at,
    is_pinned: card.is_pinned || false,
    id: card.id || card.cardid,
    career_card_date: card.career_card_date,
    career_impact: card.career_impact || { value: 3, last_updated: card.created_at },
    engagement_level: card.engagement_level || { value: 3, last_updated: card.created_at }
  };

  return {
    id: card.cardid,
    title: cardData.title,
    type: 'card',
    custom_tags: cardData.custom_tags,
    date_tags: cardData.date_tags,
    created_at: card.created_at,
    is_pinned: card.is_pinned || false,
    card: fullCard // Include the full card data
  };
};

interface UseCareerCardsOptions {
  filterByLinks?: {
    targetType: string;
    targetId?: string;
    metadata?: Record<string, any>;
  };
}

export const useCareerCards = (options?: UseCareerCardsOptions) => {
  const { showToast } = useToast();

  const { data: cards, error, refetch, isLoading } = useCachedData<(FilterItem & { card: CareerCard })[]>(
    async () => {
      // If filtering by links, first get the linked card IDs
      let linkedCardIds: string[] | null = null;
      
      if (options?.filterByLinks) {
        const { targetType, targetId, metadata } = options.filterByLinks;
        
        let linkQuery = supabase
          .from('content_links')
          .select('source_id')
          .eq('source_type', 'career_card')
          .eq('target_type', targetType);
        
        if (targetId) {
          linkQuery = linkQuery.eq('target_id', targetId);
        }
        
        if (metadata) {
          Object.entries(metadata).forEach(([key, value]) => {
            linkQuery = linkQuery.eq(`metadata->${key}`, value);
          });
        }
        
        const { data: links, error: linkError } = await linkQuery;
        
        if (linkError) throw linkError;
        
        linkedCardIds = links?.map(link => link.source_id) || [];
        
        // If no linked cards found, return empty array
        if (linkedCardIds.length === 0) {
          return [];
        }
      }
      
      // Build the cards query
      let query = supabase
        .from('career_cards')
        .select('*')
        .order('created_at', { ascending: false });
      
      // If we have linked card IDs, filter by them
      if (linkedCardIds) {
        query = query.in('cardid', linkedCardIds);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      // Transform and validate each card
      return (data || [])
        .map(rawCard => {
          const card = rawCard as CareerCard;
          // Ensure card_data exists and has required fields
          if (!card.card_data) {
            card.card_data = {
              title: 'Untitled Card',
              type: 'Project',
              custom_tags: [],
              date_tags: []
            };
          }
          return transformToFilterItem(card);
        })
        .filter(item => item !== null);
    },
    {
      cacheKey: options?.filterByLinks 
        ? `${CACHE_KEYS.CAREER_CARDS}_filtered_${JSON.stringify(options.filterByLinks)}`
        : CACHE_KEYS.CAREER_CARDS,
      onError: (error) => {
        console.error('Failed to load career cards:', error);
        showToast('Failed to load career cards', 'error');
      }
    }
  );

  return { cards, error, refetch, isLoading };
};

export const useCareerCardsOld = (userId: string) => {
  const queryClient = useQueryClient();

  // Fetch cards with infinite loading
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    error
  } = useInfiniteQuery<CareerCard[]>({
    queryKey: ['careerCards', userId],
    initialPageParam: null,
    queryFn: async ({ pageParam }) => {
      let query = supabase
        .from('career_cards')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(PAGE_SIZE);

      if (pageParam) {
        query = query.lt('created_at', pageParam);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as CareerCard[];
    },
    getNextPageParam: (lastPage: CareerCard[]) => {
      if (lastPage.length < PAGE_SIZE) return undefined;
      return lastPage[lastPage.length - 1].created_at;
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep data in cache for 30 minutes
  });

  // Save card mutation
  const saveCard = useMutation({
    mutationFn: async ({ card, isNew }: { card: CareerCard; isNew: boolean }) => {
      if (!card.card_data) {
        throw new Error('Card data is required');
      }

      if (!card.card_version) {
        throw new Error('Card version is required');
      }

      const newDateTags = getDateTags(
        card.card_data.start_date || null,
        card.card_data.end_date || null,
        'card'
      );

      const cardData = {
        ...card.card_data,
        date_tags: newDateTags,
      };

      try {
        if (isNew) {
          const { data, error } = await supabase
            .from('career_cards')
            .insert({
              user_id: userId,
              card_data: cardData,
              card_version: card.card_version,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            })
            .select();

          if (error) {
            if (error.code === '23502') { // not-null violation
              throw new Error('Required fields are missing. Please check all required fields are filled.');
            }
            throw error;
          }

          await awardXP('career_card', { action: 'create' });
          return data[0];
        } else {
          const { data, error } = await supabase
            .from('career_cards')
            .update({
              card_data: cardData,
              card_version: card.card_version,
              updated_at: new Date().toISOString(),
            })
            .eq('cardid', card.cardid)
            .select();

          if (error) {
            if (error.code === '23502') { // not-null violation
              throw new Error('Required fields are missing. Please check all required fields are filled.');
            }
            throw error;
          }

          await awardXP('career_card', {
            action: 'update',
            cardId: card.cardid
          });
          return data[0];
        }
      } catch (error) {
        console.error('Error saving card:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['careerCards', userId] });
      toast.success('Card saved successfully');
    },
    onError: (error: Error) => {
      console.error('Error saving card:', error);
      toast.error(error.message || 'Failed to save card. Please try again.');
    },
  });

  // Delete card mutation
  const deleteCard = useMutation({
    mutationFn: async (cardId: string) => {
      const { error } = await supabase
        .from('career_cards')
        .delete()
        .eq('cardid', cardId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['careerCards', userId] });
      toast.success('Card deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting card:', error);
      toast.error('Failed to delete card. Please try again.');
    },
  });

  // Get all cards from all pages
  const allCards = data?.pages.flatMap(page => page) ?? [];

  return {
    cards: allCards,
    loadMore: fetchNextPage,
    hasMore: hasNextPage,
    isLoading: status === 'pending',
    isLoadingMore: isFetchingNextPage,
    error,
    saveCard: (card: CareerCard, isNew: boolean) => saveCard.mutate({ card, isNew }),
    deleteCard: (cardId: string) => deleteCard.mutate(cardId),
  };
}; 